import { useEffect, useRef, useState } from 'react';
import { LINEAR_RGB, fromLinear, writePixel } from './deepZoomColor';

export const UNSKEW_EXAMPLES = [
  {
    id: 'unskew-armada-rigging',
    title: 'Armada rigging mini-ship',
    cx: -1.72394558969,
    cy: -0.01411417746,
    period: 12,
    stretch: 13.0,
    span: 0.000097694793,
    maxIterations: 1000,
    kappa: [0.382664, -0.849792, -0.383807, 3.465593],
    valueRange: [41.49, 174.25],
    gamma: 0.82,
    skewedSrc: './images/unskew-armada-rigging-skewed.webp',
    unskewedSrc: './images/unskew-armada-rigging-unskewed.webp',
    skewedAlt: '1:1 skewed view of a period-12 mini-ship in the armada rigging sheared 13x into a narrow diagonal slit',
    unskewedAlt: 'Jacobian-unskewed view of the period-12 armada rigging mini-ship restored to an upright ship inside a radial spiderweb',
    description: 'High in the rigging east of the largest ship, repeated folds stretch this mini-ship 13× along a steep diagonal, flattening its hull into a thin dark slit. Unskewing the view restores the upright ship and the radial web of arches around it.',
  },
  {
    id: 'unskew-eastern-sail-rosette',
    title: 'Eastern sail sunflower crown',
    cx: 0.889563905272,
    cy: -1.532183443745,
    period: 15,
    stretch: 9.6,
    span: 0.0000052358459,
    maxIterations: 800,
    kappa: [0.747174, -1.097167, -0.904736, 2.666909],
    valueRange: [43.59, 78.76],
    gamma: 0.85,
    skewedSrc: './images/unskew-eastern-sail-rosette-skewed.webp',
    unskewedSrc: './images/unskew-eastern-sail-rosette-unskewed.webp',
    skewedAlt: '1:1 skewed view of a period-15 embedded quasi-Julia ring in the eastern sail stretched 9.6x into diagonal flame streaks',
    unskewedAlt: 'Jacobian-unskewed view of the period-15 eastern sail embedded quasi-Julia set forming an eight-petalled sunflower crown',
    description: 'Inside the upper-right eastern sail, a ring of filaments is stretched nearly tenfold into diagonal streaks of fire around a narrow splinter. Unskewing opens those streaks into a circular eight-petalled flower framing a tiny central ship.',
  },
  {
    id: 'unskew-scalloped-medallion',
    title: 'Scalloped medallion ring',
    cx: -1.728375605356,
    cy: -0.016699227161,
    period: 23,
    stretch: 8.7,
    span: 1.6481089e-7,
    maxIterations: 1000,
    kappa: [0.490974, -0.796203, -0.453186, 2.77169],
    valueRange: [63.16, 163],
    gamma: 0.82,
    skewedSrc: './images/unskew-scalloped-medallion-skewed.webp',
    unskewedSrc: './images/unskew-scalloped-medallion-unskewed.webp',
    skewedAlt: '1:1 skewed view of a period-23 period-doubled medallion crushed 8.7x into dense diagonal stripes',
    unskewedAlt: 'Jacobian-unskewed view of the period-23 medallion revealing eight heart-shaped bays and concentric scalloped arches',
    description: 'Deeper in the rigging, twenty-three steps of folding compress a circular medallion almost 9× into a dense wall of diagonal stripes. Unskewing reveals eight heart-shaped bays and concentric scalloped rings around the center.',
  },
];

/**
 * Interpolate smoothly between the 1:1 identity matrix (t = 0) and the
 * Jacobian unskew matrix kappa (t = 1), normalizing by sqrt(|det|) so the
 * viewport area stays constant throughout the transition.
 */
export function interpolateUnskewMatrix(kappa, t) {
  const clamped = Math.max(0, Math.min(1, Number(t) || 0));
  if (clamped === 0) return [1, 0, 0, 1];
  if (clamped === 1) return [kappa[0], kappa[1], kappa[2], kappa[3]];
  const omt = 1 - clamped;
  const m00 = omt + clamped * kappa[0];
  const m01 = clamped * kappa[1];
  const m10 = clamped * kappa[2];
  const m11 = omt + clamped * kappa[3];
  const det = Math.abs(m00 * m11 - m01 * m10);
  const scale = det > 1e-12 ? 1 / Math.sqrt(det) : 1;
  return [m00 * scale, m01 * scale, m10 * scale, m11 * scale];
}

const iterScratch = { escaped: false, iterations: 0, distanceSquared: 0, glitch: false };
const samplePixelScratch = new Uint8ClampedArray(4);
const SUB_2X2 = [[-0.25, -0.25], [0.25, -0.25], [-0.25, 0.25], [0.25, 0.25]];

function evaluatePoint(x0, y0, maxIterations) {
  let x = 0;
  let y = 0;
  let x2 = 0;
  let y2 = 0;
  let i = 0;
  while (x2 + y2 < 4 && i < maxIterations) {
    const xy = x * y;
    x = x2 - y2 + x0;
    y = (xy < 0 ? -2 * xy : 2 * xy) + y0;
    x2 = x * x;
    y2 = y * y;
    i++;
  }
  iterScratch.escaped = x2 + y2 >= 4;
  iterScratch.iterations = iterScratch.escaped ? i : maxIterations;
  iterScratch.distanceSquared = x2 + y2;
  return iterScratch;
}

export function renderUnskewedRgba(pixels, width, height, example, t, samplesPerAxis = 1) {
  const [m00, m01, m10, m11] = interpolateUnskewMatrix(example.kappa, t);
  const { cx, cy, span, maxIterations, valueRange, gamma } = example;
  const colorParams = { palette: 'warm', useMu: true, valueRange, gamma };

  if (samplesPerAxis <= 1) {
    for (let py = 0; py < height; py++) {
      const uy = ((py + 0.5) / height - 0.5) * span;
      for (let px = 0; px < width; px++) {
        const ux = ((px + 0.5) / width - 0.5) * span;
        const res = evaluatePoint(
          cx + m00 * ux + m01 * uy,
          cy + m10 * ux + m11 * uy,
          maxIterations,
        );
        writePixel(pixels, (py * width + px) * 4, res, colorParams);
      }
    }
    return pixels;
  }

  for (let py = 0; py < height; py++) {
    for (let px = 0; px < width; px++) {
      let rSum = 0;
      let gSum = 0;
      let bSum = 0;
      for (let s = 0; s < SUB_2X2.length; s++) {
        const [sx, sy] = SUB_2X2[s];
        const ux = ((px + 0.5 + sx) / width - 0.5) * span;
        const uy = ((py + 0.5 + sy) / height - 0.5) * span;
        const res = evaluatePoint(
          cx + m00 * ux + m01 * uy,
          cy + m10 * ux + m11 * uy,
          maxIterations,
        );
        writePixel(samplePixelScratch, 0, res, colorParams);
        rSum += LINEAR_RGB[samplePixelScratch[0]];
        gSum += LINEAR_RGB[samplePixelScratch[1]];
        bSum += LINEAR_RGB[samplePixelScratch[2]];
      }
      const offset = (py * width + px) * 4;
      pixels[offset] = fromLinear(rSum * 0.25);
      pixels[offset + 1] = fromLinear(gSum * 0.25);
      pixels[offset + 2] = fromLinear(bSum * 0.25);
      pixels[offset + 3] = 255;
    }
  }
  return pixels;
}

export function JacobianUnskewExample({ example }) {
  const [skewAmount, setSkewAmount] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const canvasRef = useRef(null);
  const imageDataRef = useRef(null);

  useEffect(() => {
    if (!hasInteracted || skewAmount === 0 || skewAmount === 1) return undefined;
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== 'function') return undefined;
    let context = null;
    try {
      context = canvas.getContext('2d');
    } catch {
      context = null;
    }
    if (!context || typeof context.createImageData !== 'function' || typeof context.putImageData !== 'function') {
      return undefined;
    }
    const width = canvas.width;
    const height = canvas.height;
    if (!imageDataRef.current || imageDataRef.current.width !== width || imageDataRef.current.height !== height) {
      imageDataRef.current = context.createImageData(width, height);
    }
    renderUnskewedRgba(imageDataRef.current.data, width, height, example, skewAmount, 1);
    context.putImageData(imageDataRef.current, 0, 0);

    if (globalThis.IS_REACT_ACT_ENVIRONMENT) return undefined;
    const timer = setTimeout(() => {
      renderUnskewedRgba(imageDataRef.current.data, width, height, example, skewAmount, 2);
      context.putImageData(imageDataRef.current, 0, 0);
    }, 65);
    return () => clearTimeout(timer);
  }, [example, hasInteracted, skewAmount]);

  const sliderId = `${example.id}-slider`;
  const percent = Math.round(skewAmount * 100);
  const activeImgSrc = skewAmount === 1 ? example.unskewedSrc : example.skewedSrc;
  const activeImgAlt = skewAmount === 1 ? example.unskewedAlt : example.skewedAlt;
  const showCanvas = hasInteracted && skewAmount > 0 && skewAmount < 1;

  return (
    <div id={example.id} className="unskew-example">
      <h3 className="unskew-example-title">{example.title}</h3>
      <p>{example.description}</p>
      <div className="unskew-comparison-grid">
        <div className="unskew-column unskew-column-left">
          <div className="unskew-image-frame">
            <img
              src={activeImgSrc}
              alt={activeImgAlt}
              loading="lazy"
              decoding="async"
            />
            <canvas
              ref={canvasRef}
              width={240}
              height={240}
              className={`unskew-live-canvas${showCanvas ? ' active' : ''}`}
              aria-hidden="true"
            />
          </div>
          <div className="unskew-slider-controls" data-nosnippet="">
            <label htmlFor={sliderId} className="unskew-slider-label">
              <span className="unskew-slider-value">{`${percent}% unskewed`}</span>
            </label>
            <input
              id={sliderId}
              type="range"
              className="unskew-slider"
              min="0"
              max="1"
              step="0.01"
              value={skewAmount}
              aria-label={`Adjust skew for ${example.title}`}
              onChange={event => {
                setHasInteracted(true);
                setSkewAmount(Number(event.target.value));
              }}
            />
          </div>
        </div>
        <div className="unskew-column unskew-column-right">
          <div className="unskew-image-frame">
            <img
              src={example.unskewedSrc}
              alt={example.unskewedAlt}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="unskew-reference-caption" data-nosnippet="">
            Unskewed reference (&kappa; = &beta;<sup>&minus;1</sup>)
          </div>
        </div>
      </div>
    </div>
  );
}
